import { httpClient } from '../http/httpClient.js';
import { buildQueryString, getCollection } from './serviceUtils.js';

const TEAMS_API_URL = '/api/teams';
const ON_DUTY_API_URL = `${TEAMS_API_URL}/onDuty`;
const TEAM_LIMITS_API_URL = `${TEAMS_API_URL}/limits`;
const TEAM_LOADS_API_URL = `${TEAMS_API_URL}/loads`;

const buildTeamsUrl = (params = {}) => {
  const queryString = buildQueryString(params);
  return queryString ? `${TEAMS_API_URL}?${queryString}` : TEAMS_API_URL;
};

const buildOnDutyUrl = (params = {}) => {
  const queryString = buildQueryString(params);
  return queryString ? `${ON_DUTY_API_URL}?${queryString}` : ON_DUTY_API_URL;
};

const buildTeamLimitsUrl = (teamId, categoryName) => {
  const queryString = buildQueryString({ teamId });
  const base = categoryName
    ? `${TEAM_LIMITS_API_URL}/${encodeURIComponent(categoryName)}`
    : TEAM_LIMITS_API_URL;
  return queryString ? `${base}?${queryString}` : base;
};

const buildTeamLoadsUrl = (params = {}) => {
  const queryString = buildQueryString(params);
  return queryString ? `${TEAM_LOADS_API_URL}?${queryString}` : TEAM_LOADS_API_URL;
};

const normalizeLoadByCategory = (rawLoadByCategory) => {
  if (!rawLoadByCategory || typeof rawLoadByCategory !== 'object') {
    return {};
  }

  return Object.entries(rawLoadByCategory).reduce((accumulator, [categoryName, count]) => {
    const normalizedCount = Number(count) || 0;

    if (normalizedCount > 0) {
      accumulator[categoryName] = normalizedCount;
    }

    return accumulator;
  }, {});
};

const hasPaginationParams = (params = {}) => {
  return params.pageSize !== undefined || params.pageCurrent !== undefined;
};

const buildTeamsResponse = (payload) => ({
  teams: getCollection(payload, ['teams', 'data', 'responses']),
  pagination: payload?.pagination || null,
  payload
});

export const teamService = {
  getTeams: async (params = {}) => {
    const payload = await httpClient.get(buildTeamsUrl(params));
    const teamsResponse = buildTeamsResponse(payload);

    if (!hasPaginationParams(params)) {
      return teamsResponse.teams;
    }

    return teamsResponse;
  },

  createTeam: async (teamData) => {
    return httpClient.post(TEAMS_API_URL, { data: teamData });
  },

  updateTeam: async (teamId, teamData) => {
    return httpClient.put(`${TEAMS_API_URL}/${encodeURIComponent(teamId)}`, {
      data: teamData
    });
  },

  deleteTeam: async (teamId) => {
    return httpClient.delete(`${TEAMS_API_URL}/${encodeURIComponent(teamId)}`);
  },

  getTeamLimits: async (teamId) => {
    const payload = await httpClient.get(buildTeamLimitsUrl(teamId));
    return getCollection(payload, ['limits', 'categories']);
  },

  updateTeamLimit: async (teamId, categoryName, taskLimit) => {
    return httpClient.put(buildTeamLimitsUrl(teamId, categoryName), {
      data: { taskLimit }
    });
  },

  // Devuelve un mapa por teamId: las cards del resumen del calendario se emparejan
  // con sus columnas por identificador, no por nombre (dos equipos pueden llamarse igual).
  getTeamLoads: async (scheduledDate) => {
    const payload = await httpClient.get(buildTeamLoadsUrl({ scheduledDate }));
    const loads = getCollection(payload, ['loads', 'teams', 'data', 'responses']);

    return loads.reduce((accumulator, load) => {
      const teamId = load?.teamId || load?.id;

      if (!teamId) {
        return accumulator;
      }

      accumulator[teamId] = {
        teamId,
        tasksLoad: Number(load?.tasksLoad) || 0,
        loadByCategory: normalizeLoadByCategory(load?.loadByCategory)
      };

      return accumulator;
    }, {});
  },

  getOnDuty: async (params = {}) => {
    return httpClient.get(buildOnDutyUrl(params));
  },

  createOnDuty: async (payload) => {
    return httpClient.post(ON_DUTY_API_URL, { data: payload });
  },

  deleteOnDuty: async (payload) => {
    return httpClient.delete(ON_DUTY_API_URL, { data: payload });
  },

  restoreOnDuty: async (payload) => {
    return httpClient.patch(ON_DUTY_API_URL, { data: payload });
  },

  interruptOnDuty: async (payload) => {
    return httpClient.patch(`${ON_DUTY_API_URL}/interrupt`, { data: payload });
  }
};
